import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdmin } from '../context/AdminContext';

import dzireEtiosImg from '../assets/images/dzire-etios.jpg';
import innovaImg from '../assets/images/innova.jpg';
import crystaImg from '../assets/images/crysta.jpg';
import corollaImg from '../assets/images/corolla.jpg';
import tempo11Img from '../assets/images/tempo-11-seater.jpg';
import tempo18Img from '../assets/images/tempo-18-seater.jpg';
import tempoLuxuryImg from '../assets/images/tempo-luxury.jpg';

// ─── Font injection (same as Home) ────────────────────────────────────────────
const injectLink = (href) => {
  if (!document.querySelector(`link[href="${href}"]`)) {
    const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
    document.head.appendChild(l);
  }
};
injectLink('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&display=swap');
injectLink('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

// ─── Design tokens (identical to Home.jsx) ────────────────────────────────────
const T = {
  bg:      '#FAFAF7',
  bgWarm:  '#F2EDE4',
  bgCard:  '#FFFFFF',
  bgDark:  '#0E1525',
  text:    '#111827',
  soft:    '#6B7280',
  softer:  '#9CA3AF',
  accent:  '#FDDB1C',
  accentD: '#E8C900',
  border:  '#E8E2D8',
  borderL: '#F0EBE2',
  green:   '#10B981',
  blue:    '#2563EB',
  display: "'Lora', Georgia, serif",
  sans:    "'Bricolage Grotesque', system-ui, sans-serif",
};

// ─── Section Intro (identical to Home) ────────────────────────────────────────
const SectionIntro = ({ tag, title, subtitle, center = false, light = false }) => (
  <div style={{ textAlign: center ? 'center' : 'left', marginBottom: '48px' }}>
    {tag && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: center ? 'center' : 'flex-start', marginBottom: '12px' }}>
        <span style={{ width: '22px', height: '2px', background: T.accent, borderRadius: '1px', display: 'block' }} />
        <span style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: light ? 'rgba(255,255,255,0.45)' : T.soft }}>{tag}</span>
      </div>
    )}
    <h2 style={{ fontFamily: T.display, fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: 500, color: light ? '#fff' : T.text, margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.4px' }}>
      {title}
    </h2>
    {subtitle && (
      <p style={{ fontFamily: T.sans, fontSize: '15px', color: light ? 'rgba(255,255,255,0.5)' : T.soft, lineHeight: 1.75, margin: 0, maxWidth: center ? '480px' : '460px', marginLeft: center ? 'auto' : 0, marginRight: center ? 'auto' : 0 }}>
        {subtitle}
      </p>
    )}
  </div>
);

// ─── Vehicle Card with Image Overlay Badges ───────────────────────────────────
const VehicleCard = ({ car, isHovered, onHover, onBook, isMobile }) => {
  const active = isHovered;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true, margin: '-50px' }}
      onMouseEnter={() => onHover(car.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        background: active ? T.bgDark : T.bgCard,
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.35s cubic-bezier(0.2, 0.9, 0.4, 1)',
        boxShadow: active ? '0 20px 40px rgba(14,21,37,0.25)' : '0 4px 12px rgba(0,0,0,0.04)',
        transform: active ? 'translateY(-8px)' : 'translateY(0)',
        border: `1.5px solid ${active ? T.accent : T.border}`,
        willChange: 'transform',
      }}>

      {/* Image Section with Overlay Badges */}
      <div style={{
        background: '#FFFFFF',
        padding: isMobile ? '20px' : '28px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderBottom: `1px solid ${active ? 'rgba(255,255,255,0.1)' : T.border}`,
        position: 'relative', height: '180px',
      }}>
        {/* Seat Badge - Left Side */}
        <div style={{
          position: 'absolute', top: '12px', left: '12px',
          display: 'flex', alignItems: 'center', gap: '6px',
          background: active ? 'rgba(14,21,37,0.85)' : 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
          borderRadius: '30px', 
          padding: '5px 12px',
          zIndex: 2,
          border: `1px solid ${active ? T.accent : 'rgb(255, 255, 255)'}`,
        }}>
          <i className="fas fa-users" style={{ fontSize: '10px', color: active ? T.accent : '#fff' }} />
          <span style={{ 
            fontFamily: T.sans, 
            fontSize: '11px', 
            fontWeight: 700, 
            color: active ? T.accent : '#fff',
            letterSpacing: '0.3px'
          }}>
            {car.capacity} Seater
          </span>
        </div>

        {/* Category Badge - Right Side */}
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          background: active ? T.accent : 'rgba(253,219,28,0.95)',
          backdropFilter: 'blur(4px)',
          borderRadius: '30px', 
          padding: '5px 14px',
          zIndex: 2,
          boxShadow: active ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
        }}>
          <span style={{ 
            fontFamily: T.sans, 
            fontSize: '11px', 
            fontWeight: 800, 
            color: active ? T.bgDark : T.bgDark,
            letterSpacing: '0.3px'
          }}>
            {car.category}
          </span>
        </div>

        {/* Vehicle Image */}
        <img
          src={car.image} alt={car.name} loading="lazy"
          style={{
            width: '100%', height: '100%', objectFit: 'contain',
            transition: 'transform 0.4s ease',
            transform: active ? 'scale(1.08)' : 'scale(1)',
            filter: active ? 'drop-shadow(0 8px 24px rgba(0,0,0,0.3))' : 'none',
          }}
        />
      </div>

      {/* Content */}
      <div style={{ padding: isMobile ? '18px' : '22px' }}>

        {/* Name + price */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontFamily: T.sans, fontWeight: 700, fontSize: isMobile ? '15px' : '17px', color: active ? '#fff' : T.text, margin: '0 0 4px', lineHeight: 1.2 }}>
              {car.name}
            </h3>
            <div style={{ fontFamily: T.sans, fontSize: '11px', color: active ? 'rgba(255,255,255,0.4)' : T.soft }}>
              Driver · Fuel · AC incl.
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: T.sans, fontSize: isMobile ? '22px' : '26px', fontWeight: 800, color: active ? T.accent : T.accentD, letterSpacing: '-0.5px', lineHeight: 1 }}>
              ₹{car.startingPrice}
            </div>
            <div style={{ fontFamily: T.sans, fontSize: '10px', color: active ? 'rgba(255,255,255,0.35)' : T.soft, marginTop: '2px' }}>
              starting/km
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: active ? 'rgba(255,255,255,0.08)' : T.border, marginBottom: '16px' }} />

        {/* Local Package */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '9px' }}>
            <i className="fas fa-map-marker-alt" style={{ fontSize: '10px', color: T.accent }} />
            <span style={{ fontFamily: T.sans, fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: active ? 'rgba(255,255,255,0.5)' : T.soft }}>
              Local Package
            </span>
          </div>

          {/* 4hr + 8hr */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
            {car.prices.slice(0, 2).map((price, i) => (
              <div key={i} style={{
                background: active ? 'rgba(255,255,255,0.06)' : T.bgWarm,
                border: `1px solid ${active ? 'rgba(255,255,255,0.1)' : T.borderL}`,
                borderRadius: '10px', padding: '9px 11px',
              }}>
                <div style={{ fontFamily: T.sans, fontSize: '10px', color: active ? 'rgba(255,255,255,0.4)' : T.soft, marginBottom: '3px' }}>{price.package}</div>
                <div style={{ fontFamily: T.sans, fontSize: '15px', fontWeight: 800, color: active ? T.accent : T.blue }}>{price.price}</div>
              </div>
            ))}
          </div>

          {/* Extra km + hour */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {car.prices.slice(2, 4).map((price, i) => (
              <div key={i} style={{
                flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '7px 10px',
                background: active ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                borderRadius: '8px', border: `1px solid ${active ? 'rgba(255,255,255,0.07)' : T.borderL}`,
              }}>
                <span style={{ fontFamily: T.sans, fontSize: '10px', color: active ? 'rgba(255,255,255,0.35)' : T.softer }}>{price.package}</span>
                <span style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 700, color: active ? 'rgba(255,255,255,0.65)' : T.soft }}>{price.price}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Outstation */}
        {car.outstation && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '9px' }}>
              <i className="fas fa-road" style={{ fontSize: '10px', color: T.blue }} />
              <span style={{ fontFamily: T.sans, fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: active ? 'rgba(245, 242, 242, 0.5)' : T.soft }}>
                Outstation
              </span>
            </div>
            <div style={{
              background: active ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.04)',
              border: `1px solid ${active ? 'rgba(37,99,235,0.3)' : 'rgba(37,99,235,0.12)'}`,
              borderRadius: '10px', padding: '11px 12px',
              display: 'flex', flexDirection: 'column', gap: '6px',
            }}>
              {[
                { label: 'Minimum',     value: car.outstation.minimum },
                { label: 'Extra km',    value: car.outstation.extraKm },
                { label: 'Driver bata', value: car.outstation.bata },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: T.sans, fontSize: '11px', color: active ? 'rgba(255,255,255,0.4)' : T.soft }}>{row.label}</span>
                  <span style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 700, color: active ? '#91B4FF' : T.blue }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Book button — appears on hover like Home fleet */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: active ? 1 : 0, y: active ? 0 : 10 }} transition={{ duration: 0.25 }}>
          <button
            onClick={() => onBook(car.name)}
            style={{
              width: '100%', background: T.accent, border: 'none',
              padding: '12px', borderRadius: '12px',
              fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.bgDark,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', transition: 'opacity 0.2s',
              boxShadow: '0 4px 16px rgba(253,219,28,0.35)',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            <i className="fas fa-phone" style={{ fontSize: '11px' }} /> Book Now
          </button>
        </motion.div>

        {/* Subtle call hint when not hovered */}
        <motion.div
          animate={{ opacity: active ? 0 : 1, y: active ? 4 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', pointerEvents: active ? 'none' : 'auto' }}>
          <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 600, color: T.soft, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px', transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.text}
            onMouseLeave={e => e.currentTarget.style.color = T.soft}>
            <i className="fas fa-phone-alt" style={{ fontSize: '9px' }} /> Hover to Book
          </a>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ─── Main Prices Component ─────────────────────────────────────────────────────
const Prices = () => {
  const [activeTab, setActiveTab]   = useState('All cars');
  const [hoveredCar, setHoveredCar] = useState(null);
  const [isMobile, setIsMobile]     = useState(window.innerWidth <= 768);
  const [isSmall, setIsSmall]       = useState(window.innerWidth <= 480);
  const { cars }                    = useAdmin();

  const tabs = ['All cars', 'Sedan', 'SUV', 'Tempo'];
  const maxW = '1280px';

  const defaultCars = [
    {
      id: '1', name: 'Dzire / Etios', category: 'Sedan', image: dzireEtiosImg, capacity: 4, startingPrice: '12',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹1,300' },
        { package: '8 Hours · 80 Kms', price: '₹2,600' },
        { package: 'Extra Km',         price: '₹14/km' },
        { package: 'Extra Hour',       price: '₹300/hr' },
      ],
      outstation: { minimum: '250 KMS / day', extraKm: '₹14 per km', bata: '₹600 per day' },
    },
    {
      id: '2', name: 'Innova', category: 'SUV', image: innovaImg, capacity: 7, startingPrice: '15',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹1,800' },
        { package: '8 Hours · 80 Kms', price: '₹3,600' },
        { package: 'Extra Km',         price: '₹18/km' },
        { package: 'Extra Hour',       price: '₹400/hr' },
      ],
      outstation: { minimum: '250 KMS / day', extraKm: '₹18 per km', bata: '₹700 per day' },
    },
    {
      id: '3', name: 'Crysta', category: 'SUV', image: crystaImg, capacity: 7, startingPrice: '18',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹2,000' },
        { package: '8 Hours · 80 Kms', price: '₹4,000' },
        { package: 'Extra Km',         price: '₹20/km' },
        { package: 'Extra Hour',       price: '₹450/hr' },
      ],
      outstation: { minimum: '250 KMS / day', extraKm: '₹20 per km', bata: '₹700 per day' },
    },
    {
      id: '4', name: 'Corolla', category: 'Sedan', image: corollaImg, capacity: 5, startingPrice: '20',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹2,400' },
        { package: '8 Hours · 80 Kms', price: '₹4,800' },
        { package: 'Extra Km',         price: '₹28/km' },
        { package: 'Extra Hour',       price: '₹600/hr' },
      ],
      outstation: { minimum: '250 KMS / day', extraKm: '₹28 per km', bata: '₹700 per day' },
    },
    {
      id: '5', name: 'Tempo 11 Seater', category: 'Tempo', image: tempo11Img, capacity: 11, startingPrice: '22',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹2,500' },
        { package: '8 Hours · 80 Kms', price: '₹5,000' },
        { package: 'Extra Km',         price: '₹22/km' },
        { package: 'Extra Hour',       price: '₹600/hr' },
      ],
      outstation: { minimum: '250 KMS / day', extraKm: '₹22 per km', bata: '₹800 per day' },
    },
    {
      id: '6', name: 'Tempo 18 Seater', category: 'Tempo', image: tempo18Img, capacity: 18, startingPrice: '26',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹3,000' },
        { package: '8 Hours · 80 Kms', price: '₹6,000' },
        { package: 'Extra Km',         price: '₹26/km' },
        { package: 'Extra Hour',       price: '₹700/hr' },
      ],
      outstation: { minimum: '300 KMS / day', extraKm: '₹26 per km', bata: '₹900 per day' },
    },
    {
      id: '7', name: 'Tempo 11 Luxury', category: 'Tempo', image: tempoLuxuryImg, capacity: 11, startingPrice: '29',
      prices: [
        { package: '4 Hours · 40 Kms', price: '₹4,000' },
        { package: '8 Hours · 80 Kms', price: '₹7,500' },
        { package: 'Extra Km',         price: '₹29/km' },
        { package: 'Extra Hour',       price: '₹750/hr' },
      ],
      outstation: { minimum: '250 KMS / day', extraKm: '₹29 per km', bata: '₹900 per day' },
    },
  ];

  const displayCars  = cars && cars.length > 0 ? cars : defaultCars;
  const filteredCars = activeTab === 'All cars'
    ? displayCars
    : displayCars.filter(c => c.category === activeTab);

  useEffect(() => { document.title = 'Saha Travels | Rentals'; }, []);
  useEffect(() => {
    const r = () => { setIsMobile(window.innerWidth <= 768); setIsSmall(window.innerWidth <= 480); };
    window.addEventListener('resize', r); return () => window.removeEventListener('resize', r);
  }, []);

  const handleBook = () => { window.location.href = 'tel:+918637658382'; };

  return (
    <>
      {/* ══ NAV (identical to Home) ══════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(250,250,247,0.94)', backdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${T.border}`,
        padding: isMobile ? '14px 20px' : '15px 60px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        willChange: 'transform',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <div style={{ width: '30px', height: '30px', background: T.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-car" style={{ fontSize: '12px', color: T.text }} />
          </div>
          <span style={{ fontFamily: T.sans, fontWeight: 800, fontSize: '17px', color: T.text, letterSpacing: '-0.3px' }}>SAHA</span>
          {!isMobile && <span style={{ fontFamily: T.sans, fontSize: '13px', color: T.softer }}>Travels</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '28px' }}>
          {!isMobile && ['Fleet', 'About', 'Contact'].map(n => (
            <a key={n} href={`#${n.toLowerCase()}`} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 500, color: T.soft, textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = T.text}
              onMouseLeave={e => e.target.style.color = T.soft}>{n}</a>
          ))}
          <a href="tel:+918637658382" style={{
            fontFamily: T.sans, fontSize: '13px', fontWeight: 700, background: T.accent, color: T.text,
            padding: isMobile ? '9px 18px' : '10px 22px', borderRadius: '50px', textDecoration: 'none',
            display: 'flex', alignItems: 'center', gap: '7px',
            boxShadow: '0 3px 14px rgba(253,219,28,0.4)', transition: 'transform 0.18s, box-shadow 0.18s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(253,219,28,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 14px rgba(253,219,28,0.4)'; }}>
            <i className="fas fa-phone" style={{ fontSize: '11px' }} />{isMobile ? 'Book' : 'Book a Ride'}
          </a>
        </div>
      </nav>

      {/* ══ HERO ═════════════════════════════════════════════════════════════ */}
      <section style={{ minHeight: '100vh', background: T.bg, paddingTop: '78px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', top: '-100px', right: isMobile ? '-120px' : '-80px', width: isMobile ? '380px' : '680px', height: isMobile ? '380px' : '680px', borderRadius: '50%', background: T.accent, opacity: 0.11, filter: 'blur(100px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: '-80px', width: '360px', height: '360px', borderRadius: '50%', background: T.blue, opacity: 0.06, filter: 'blur(80px)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: maxW, margin: '0 auto', width: '100%', padding: isMobile ? '40px 20px 60px' : '40px 60px 60px', position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: '50px', padding: '7px 14px 7px 9px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: T.green, display: 'block', boxShadow: `0 0 0 3px rgba(16,185,129,0.2)`, animation: 'pulse-green 2s infinite' }} />
            <span style={{ fontFamily: T.sans, fontSize: '12px', fontWeight: 600, color: T.text }}>Transparent Pricing · All Vehicles Listed</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            style={{ fontFamily: T.display, fontSize: isMobile ? '40px' : 'clamp(3rem, 5.5vw, 4.8rem)', fontWeight: 600, lineHeight: 1.07, color: T.text, margin: '0 0 18px', letterSpacing: '-1.5px' }}>
            Simple Rates,<br />
            <em style={{ fontStyle: 'italic', fontWeight: 400 }}>No</em>{' '}
            <span style={{ position: 'relative', display: 'inline-block' }}>
              Surprises.
              <span style={{ position: 'absolute', bottom: '2px', left: 0, width: '100%', height: '9px', background: T.accent, zIndex: -1, opacity: 0.45, borderRadius: '3px' }} />
            </span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            style={{ fontFamily: T.sans, fontSize: '15.5px', color: T.soft, lineHeight: 1.76, margin: '0 0 32px', maxWidth: '480px' }}>
            Choose your vehicle, see the full breakdown. Every rate includes driver, fuel, and AC. Toll &amp; parking billed separately.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '40px' }}>
            <a href="#vehicles" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '14px 28px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 20px rgba(253,219,28,0.38)', transition: 'transform 0.18s, box-shadow 0.18s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(253,219,28,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(253,219,28,0.38)'; }}>
              <i className="fas fa-car" style={{ fontSize: '12px' }} /> View All Vehicles
            </a>
            <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 600, background: 'transparent', color: T.text, padding: '14px 24px', borderRadius: '50px', textDecoration: 'none', border: `1.5px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: '8px', transition: 'border-color 0.2s, background 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentD; e.currentTarget.style.background = 'rgba(253,219,28,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = 'transparent'; }}>
              <i className="fas fa-phone" style={{ fontSize: '11px' }} /> Call for Quote
            </a>
          </motion.div>

          {/* Stats — same as Home */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.44 }}
            style={{ display: 'flex', gap: isMobile ? '20px' : '36px', flexWrap: 'wrap', paddingTop: '28px', borderTop: `1px solid ${T.borderL}` }}>
            {[['₹12', 'Starting/km'], ['7', 'Vehicles'], ['250 km', 'Min outstation'], ['24/7', 'Available']].map(([v, l]) => (
              <div key={l}>
                <div style={{ fontFamily: T.sans, fontSize: isMobile ? '21px' : '26px', fontWeight: 800, color: T.text, letterSpacing: '-0.5px', lineHeight: 1 }}>{v}</div>
                <div style={{ fontFamily: T.sans, fontSize: '11px', color: T.soft, marginTop: '4px' }}>{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
          style={{ position: 'absolute', bottom: '28px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', zIndex: 2 }}>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}
            style={{ width: '1px', height: '44px', background: `linear-gradient(to bottom, ${T.border}, transparent)` }} />
          <span style={{ fontFamily: T.sans, fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: T.softer }}>Scroll</span>
        </motion.div>
      </section>

      {/* ══ MARQUEE STRIP (identical to Home) ════════════════════════════════ */}
      <div style={{ background: T.accent, overflow: 'hidden', padding: '13px 0', borderTop: `1px solid ${T.accentD}`, borderBottom: `1px solid ${T.accentD}` }}>
        <div style={{ display: 'flex', animation: 'marquee 24s linear infinite', whiteSpace: 'nowrap' }}>
          {Array(3).fill(['✦ Transparent Pricing', '✦ Driver Included', '✦ AC All Vehicles', '✦ Zero Hidden Charges', '✦ 4 hrs to Full Day', '✦ Outstation Available', '✦ Tamil Nadu Coverage', '✦ 24/7 Booking']).flat().map((t, i) => (
            <span key={i} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.text, padding: '0 28px', letterSpacing: '0.02em' }}>{t}</span>
          ))}
        </div>
      </div>

      {/* ══ VEHICLE GRID ═════════════════════════════════════════════════════ */}
      <section id="vehicles" style={{ background: T.bg, padding: isMobile ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro
            tag="Our Fleet & Rates"
            title={<>Choose Your Ride<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>From ₹12/km</em></>}
            subtitle="Affordable, reliable transportation for every journey across Chennai and Tamil Nadu."
          />

          {/* Tab strip */}
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}
            style={{ display: 'flex', gap: '8px', flexWrap: isMobile ? 'wrap' : 'nowrap', marginBottom: '40px' }}>
            {tabs.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                style={{
                  padding: isMobile ? '9px 18px' : '10px 24px',
                  background: activeTab === tab ? T.bgDark : T.bgCard,
                  color: activeTab === tab ? T.accent : T.text,
                  border: `1.5px solid ${activeTab === tab ? T.accent : T.border}`,
                  borderRadius: '40px',
                  fontFamily: T.sans, fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.25s',
                  boxShadow: activeTab === tab ? `0 6px 18px rgba(253,219,28,0.18)` : 'none',
                }}>
                {tab}
              </button>
            ))}
          </motion.div>

          {/* Cards grid — auto-fill same as Home fleet */}
          <motion.div layout style={{
            display: 'grid',
            gridTemplateColumns: isSmall ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: isMobile ? '16px' : '24px',
          }}>
            <AnimatePresence mode="popLayout">
              {filteredCars.map((car, i) => (
                <motion.div key={car.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.3, delay: i * 0.06 }}>
                  <VehicleCard
                    car={car}
                    isHovered={hoveredCar === car.id}
                    onHover={setHoveredCar}
                    onBook={handleBook}
                    isMobile={isMobile}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* ══ INCLUDED FEATURES (matches Home "Why SAHA" grid) ════════════════ */}
      <section style={{ background: T.bgWarm, padding: isMobile ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro
            tag="How it Works"
            title={<>What's Always<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>Included</em></>}
            subtitle="Every rate is transparent. No hidden charges, no surprises — ever."
            center
          />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '12px' : '16px' }}>
            {[
              { icon: 'fa-user-tie',          title: 'Professional Driver',  desc: 'Trained, verified, and always courteous — every ride.' },
              { icon: 'fa-gas-pump',          title: 'Fuel Included',        desc: 'All fuel costs are part of the package rate.' },
              { icon: 'fa-snowflake',         title: 'AC Always On',         desc: 'Full AC in every vehicle, every trip.' },
              { icon: 'fa-indian-rupee-sign', title: 'Clear Pricing',        desc: 'Tolls and parking billed separately — nothing else hidden.' },
            ].map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.09 }}
                viewport={{ once: true }}
                style={{ background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '16px', padding: isMobile ? '18px' : '24px', transition: 'all 0.25s ease', willChange: 'transform' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.boxShadow = `0 8px 28px rgba(253,219,28,0.14)`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ width: '42px', height: '42px', background: T.bgWarm, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', border: `1px solid ${T.border}` }}>
                  <i className={`fas ${f.icon}`} style={{ fontSize: '16px', color: T.text }} />
                </div>
                <h3 style={{ fontFamily: T.sans, fontSize: isMobile ? '13px' : '14px', fontWeight: 700, color: T.text, margin: '0 0 7px', lineHeight: 1.3 }}>{f.title}</h3>
                <p style={{ fontFamily: T.sans, fontSize: '12px', color: T.soft, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA (identical to Home) ══════════════════════════════════════════ */}
      <section id="contact" style={{ background: T.bgDark, padding: isMobile ? '68px 20px' : '96px 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: T.accent, opacity: 0.07, filter: 'blur(80px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: T.blue, opacity: 0.08, filter: 'blur(70px)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: maxW, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: isMobile ? '36px' : '60px', alignItems: 'center' }}>
            <div>
              <SectionIntro light tag="Ready to Go?" title={<>Book Your Ride<br /><em style={{ fontStyle: 'italic', color: T.accent }}>Today.</em></>} subtitle="Call us or WhatsApp — we're available 24/7 across Chennai and Tamil Nadu." />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
              <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '16px 32px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 4px 22px rgba(253,219,28,0.28)', whiteSpace: 'nowrap', transition: 'transform 0.18s, box-shadow 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(253,219,28,0.42)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 22px rgba(253,219,28,0.28)'; }}>
                <i className="fas fa-phone" /> Call to Book
              </a>
              <a href="https://wa.me/918637658382?text=Hi%20Saha%20Travels%2C%20I%20want%20to%20book%20a%20ride" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 600, background: 'rgba(255,255,255,0.07)', color: '#fff', padding: '16px 32px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', border: '1.5px solid rgba(255,255,255,0.14)', whiteSpace: 'nowrap', transition: 'background 0.2s, border-color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.12)'; e.currentTarget.style.borderColor = 'rgba(37,211,102,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}>
                <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: '15px' }} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══ GLOBAL STYLES (identical to Home) ════════════════════════════════ */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body {
          background: #FAFAF7;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        img { max-width: 100%; display: block; }
        a { transition: opacity 0.18s; }
        ::selection { background: #FDDB1C; color: #111827; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #F2EDE4; }
        ::-webkit-scrollbar-thumb { background: #C9C0B0; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #FDDB1C; }

        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 2px rgba(16,185,129,0.25); }
          50%       { box-shadow: 0 0 0 5px rgba(16,185,129,0.1); }
        }
      `}</style>
    </>
  );
};

export default Prices;